import { useMemo } from "react";
import type { Period } from "@/pages/admin/reports/page";
import { formatAdminChartLabel } from "@/pages/admin/utils/date";

interface ClicksChartProps {
  period: Period;
}

function generateDailyData(days: number, seed: number): { label: string; value: number }[] {
  const data: { label: string; value: number }[] = [];
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now - i * dayMs);
    const label = formatAdminChartLabel(date, days);

    const base = seed + Math.sin(i * 0.7 + seed) * 300 + Math.cos(i * 0.3) * 150;
    const value = Math.max(80, Math.round(base + (i % 3 === 0 ? 400 : 0)));
    data.push({ label, value });
  }
  return data;
}

export default function ClicksChart({ period }: ClicksChartProps) {
  const days = period === "7d" ? 7 : period === "30d" ? 30 : 90;
  const seed = period === "7d" ? 800 : period === "30d" ? 1200 : 1100;

  const data = useMemo(() => generateDailyData(days, seed), [days, seed]);

  const maxVal = Math.max(...data.map((d) => d.value));
  const minVal = Math.min(...data.map((d) => d.value));

  // Show only a subset of labels to avoid crowding
  const labelStep = days <= 7 ? 1 : days <= 30 ? 5 : 10;

  // Build SVG path
  const chartW = 560;
  const chartH = 160;
  const padL = 8;
  const padR = 8;
  const padT = 10;
  const padB = 10;

  const points = data.map((d, i) => {
    const x = padL + (i / (data.length - 1)) * (chartW - padL - padR);
    const y = padT + (1 - (d.value - minVal) / (maxVal - minVal + 1)) * (chartH - padT - padB);
    return { x, y, ...d };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
    .join(" ");

  const areaPath = `${linePath} L ${points[points.length - 1].x} ${chartH} L ${points[0].x} ${chartH} Z`;

  const totalClicks = data.reduce((s, d) => s + d.value, 0);

  return (
    <div>
      <div className="flex items-center gap-6 mb-4">
        <div>
          <p className="text-2xl font-bold text-gray-900">{totalClicks.toLocaleString("pt-BR")}</p>
          <p className="text-xs text-gray-400">cliques totais no período</p>
        </div>
        <div className="flex items-center gap-1 text-emerald-600 text-sm font-semibold bg-emerald-50 px-2.5 py-1 rounded-full">
          <i className="ri-arrow-up-s-line"></i>
          12.4% vs período anterior
        </div>
      </div>

      <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full" style={{ height: 160 }}>
        <defs>
          <linearGradient id="clicksGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f97316" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <line
            key={t}
            x1={padL}
            x2={chartW - padR}
            y1={padT + t * (chartH - padT - padB)}
            y2={padT + t * (chartH - padT - padB)}
            stroke="#f3f4f6"
            strokeWidth="1"
          />
        ))}
        {/* Area */}
        <path d={areaPath} fill="url(#clicksGrad)" />
        {/* Line */}
        <path d={linePath} fill="none" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* Dots on hover — just show last point */}
        <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="4" fill="#f97316" />
      </svg>

      {/* X Labels */}
      <div className="flex justify-between mt-1 px-1">
        {data.map((d, i) =>
          i % labelStep === 0 ? (
            <span key={i} className="text-xs text-gray-400" style={{ minWidth: 0 }}>
              {d.label}
            </span>
          ) : null
        )}
      </div>
    </div>
  );
}